import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "./lib/queryClient";
import { BrandBar } from "./components/Logo";
import { isPublicPath } from "./lib/publicPaths";
import { NavBar } from "./components/NavBar";
import { ThemeController } from "./components/ThemeController";
import { RequireActiveAccount } from "./components/RequireActiveAccount";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RequireAuth } from "./components/RequireAuth";
import { RequireCoach } from "./components/RequireCoach";
import { SignIn } from "./pages/SignIn";
import { Home } from "./pages/Home";
import { LegalPage } from "./pages/LegalPage";
import { CoachDashboard } from "./pages/CoachDashboard";
import { FamilyHome } from "./pages/FamilyHome";
import { FamilyMessages } from "./pages/FamilyMessages";
import { AthleteWalkthrough } from "./pages/AthleteWalkthrough";
import { Library } from "./pages/Library";
import { CoachSettings } from "./pages/CoachSettings";
import { Signup } from "./pages/Signup";
import { SignupBillingComplete } from "./pages/SignupBillingComplete";
import { AccountSettings } from "./pages/AccountSettings";
import { Messages } from "./pages/Messages";
import { NotFound } from "./pages/NotFound";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ClientDetail, ClientPlansRedirect } from "./pages/ClientDetail";
import { SessionDetail } from "./pages/SessionDetail";
import { Tutorial } from "./pages/Tutorial";

const queryClient = createQueryClient();

/**
 * The brand bar, sized by where you are: large on the signed-out pages (finding #27 -- a child needs
 * to see at a glance that they are in the right place), compact once inside the app.
 */
function AppBrandBar() {
  const { pathname } = useLocation();
  return <BrandBar size={isPublicPath(pathname) ? "large" : "small"} />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter>
          {/* Inside the router so it can see the route: public pages are pinned to light. */}
          <ThemeController />
          <AppBrandBar />
          <NavBar />
          <Routes>
            {/* Public: the landing page, sign-in, and the invite-driven signup flow, which by
                definition has no session yet. Sign-in moved off `/` so that arriving at the domain
                shows what this is rather than a password box. */}
            <Route path="/" element={<Home />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/privacy" element={<LegalPage title="Privacy Policy" />} />
            <Route path="/terms" element={<LegalPage title="Terms of Service" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/signup/:token" element={<Signup />} />
            <Route path="/signup/billing-complete" element={<SignupBillingComplete />} />

            {/* Coach-only. RequireAuth alone only asks "is anyone signed in", which left every one of
                these reachable by any signed-in user who typed the URL -- QA found a family account on
                /library being offered Add exercise and a trash icon per row. */}
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <RequireCoach>
                    <CoachDashboard />
                  </RequireCoach>
                </RequireAuth>
              }
            />
            <Route
              path="/library"
              element={
                <RequireAuth>
                  <RequireCoach>
                    <Library />
                  </RequireCoach>
                </RequireAuth>
              }
            />
            <Route
              path="/coach/settings"
              element={
                <RequireAuth>
                  <RequireCoach>
                    <CoachSettings />
                  </RequireCoach>
                </RequireAuth>
              }
            />
            <Route
              path="/coach/clients/:clientId"
              element={
                <RequireAuth>
                  <RequireCoach>
                    <ClientDetail />
                  </RequireCoach>
                </RequireAuth>
              }
            />
            {/* Plans live in the client's workspace now (its Plans tab); an old link still lands there. */}
            <Route path="/coach/clients/:clientId/plan" element={<ClientPlansRedirect />} />
            {/* One session, in full -- the same page for both audiences, reached by their own routes so
                the coach one stays behind RequireCoach and the family one behind the access gate. */}
            <Route
              path="/coach/sessions/:sessionId"
              element={
                <RequireAuth>
                  <RequireCoach>
                    <SessionDetail audience="coach" />
                  </RequireCoach>
                </RequireAuth>
              }
            />
            <Route
              path="/family/sessions/:sessionId"
              element={
                <RequireAuth>
                  <RequireActiveAccount>
                    <SessionDetail audience="family" />
                  </RequireActiveAccount>
                </RequireAuth>
              }
            />
            <Route
              path="/coach/clients/:clientId/messages"
              element={
                <RequireAuth>
                  <RequireCoach>
                    <Messages backTo="/dashboard" />
                  </RequireCoach>
                </RequireAuth>
              }
            />
            <Route
              path="/family"
              element={
                <RequireAuth>
                  <RequireActiveAccount>
                    <FamilyHome />
                  </RequireActiveAccount>
                </RequireAuth>
              }
            />
            <Route
              path="/family/settings"
              element={
                <RequireAuth>
                  {/* Deliberately ungated. This is where billing, data export and account deletion
                      live -- gating it would lock a lapsed family away from the only page that lets
                      them restart, leave, or take their data with them. */}
                  <AccountSettings />
                </RequireAuth>
              }
            />
            <Route
              path="/family/clients/:clientId/plan"
              element={
                <RequireAuth>
                  <RequireActiveAccount>
                    <AthleteWalkthrough />
                  </RequireActiveAccount>
                </RequireAuth>
              }
            />
            {/* Family-level messages, so the nav has somewhere to point (F37). */}
            <Route
              path="/family/messages"
              element={
                <RequireAuth>
                  <RequireActiveAccount>
                    <FamilyMessages />
                  </RequireActiveAccount>
                </RequireAuth>
              }
            />
            <Route
              path="/family/clients/:clientId/messages"
              element={
                <RequireAuth>
                  <RequireActiveAccount>
                    <Messages backTo="/family" />
                  </RequireActiveAccount>
                </RequireAuth>
              }
            />

            {/*
              How the app works, for whoever is reading (finding #28). Behind RequireAuth but not behind
              a role guard or the billing gate: a lapsed family looking for "how do I restart" needs to
              be able to read it, and it contains nothing that isn't already visible in the product.
            */}
            <Route
              path="/help"
              element={
                <RequireAuth>
                  <Tutorial />
                </RequireAuth>
              }
            />

            {/* Anything else. Without this a typo or a stale bookmark rendered the brand bar and an
                otherwise empty page -- no error, no way back. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
